import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { List, X, LinkedinLogo, ShareNetwork, Star } from '@phosphor-icons/react';
import axios from 'axios';
import { useSiteLogo } from '../hooks/useSiteLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'About', path: '/about' },
  { name: 'News', path: '/news' },
  { name: 'Community', path: '/community' },
  { name: 'Case Studies', path: '/case-studies' },
  { name: 'Careers', path: '/careers' },
  { name: 'Contact', path: '/contact' },
];

// Default (backwards-compat) social configuration. LinkedIn and
// Trustpilot are fixed brand links. The "generic" slot has its label
// and URL configurable from Admin → Site Settings.
const LINKEDIN_HREF = 'https://www.linkedin.com/company/north-somerset-environment';
const TRUSTPILOT_HREF = 'https://uk.trustpilot.com/review/nsecltd.co.uk';
const DEFAULT_GENERIC_HREF = 'https://www.instagram.com/nsecltd';
const DEFAULT_GENERIC_LABEL = 'Generic Socials';

export const SiteHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Which social buttons show in the header + custom generic URL/label.
  const [socialCfg, setSocialCfg] = useState({
    linkedin: true,
    generic: true,
    trustpilot: true,
    generic_url: DEFAULT_GENERIC_HREF,
    generic_label: DEFAULT_GENERIC_LABEL,
  });
  const location = useLocation();
  const logoUrl = useSiteLogo();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsMobileMenuOpen(false); }, [location]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios.get(`${API}/page-content/site_settings`).then(r => {
        if (cancelled) return;
        const s = r.data || {};
        setSocialCfg({
          linkedin: s.header_social_linkedin_enabled !== false,
          generic: s.header_social_generic_enabled !== false,
          trustpilot: s.header_social_trustpilot_enabled !== false,
          generic_url: s.header_social_generic_url || DEFAULT_GENERIC_HREF,
          generic_label: s.header_social_generic_label || DEFAULT_GENERIC_LABEL,
        });
      }).catch(() => {});
    };
    load();
    // Re-fetch whenever the admin panel signals a settings change.
    window.addEventListener('site-logo-updated', load);
    return () => {
      cancelled = true;
      window.removeEventListener('site-logo-updated', load);
    };
  }, []);

  const socialLinks = [
    socialCfg.linkedin && { key: 'linkedin', icon: LinkedinLogo, href: LINKEDIN_HREF, label: 'LinkedIn' },
    socialCfg.generic && { key: 'generic', icon: ShareNetwork, href: socialCfg.generic_url, label: socialCfg.generic_label },
    socialCfg.trustpilot && { key: 'trustpilot', icon: Star, href: TRUSTPILOT_HREF, label: 'Trustpilot' },
  ].filter(Boolean);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-neutral-100' : 'bg-white border-b border-neutral-100'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="header-logo-link">
            <img src={logoUrl} alt="North Somerset Environment" className="h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium whitespace-nowrap ${
                  location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path)) ? 'text-lime-600' : ''
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Right side: social + CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {socialLinks.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg text-neutral-400 hover:text-lime-600 hover:bg-lime-50 transition-all duration-200"
                data-testid={`social-${s.key}`}
                aria-label={s.label}
              >
                <s.icon size={18} weight="bold" />
              </a>
            ))}
            <div className="w-px h-6 bg-neutral-200 mx-1"></div>
            <Link
              to="/login"
              className="text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
              data-testid="header-login-link"
            >
              Login
            </Link>
            <Link
              to="/contact"
              className="btn-primary text-sm"
              data-testid="header-cta-button"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-neutral-600 hover:text-neutral-900"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-neutral-100"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-base font-medium py-2 ${
                    location.pathname === link.path ? 'text-lime-600' : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex items-center gap-4 pt-4 border-t border-neutral-100">
                {socialLinks.map((s) => (
                  <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer" className="text-neutral-500 hover:text-lime-600" aria-label={s.label} data-testid={`mobile-social-${s.key}`}>
                    <s.icon size={22} weight="bold" />
                  </a>
                ))}
              </div>
              <Link to="/contact" className="btn-primary text-center mt-2" data-testid="mobile-cta-button">Get a Quote</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
